import type { MemoryCompany, MemoryCompanyNode, MemoryGraph } from "./types";

/**
 * What the Graph tab draws, as data: nodes, lines and what each one's pop-up
 * says. Two drawings share one simulation — the company network and, when a
 * name is typed, that company and what it is joined to — so the opinions live
 * here, where they can be tested, and the component only moves circles.
 */

export interface TipLine {
  text: string;
  /** `ink` for the line that says what it is, `muted` for the small print. */
  tone?: "ink" | "muted" | "mono";
}

export interface Tip {
  title: string;
  lines: TipLine[];
}

export interface DrawNode {
  name: string;
  /** The legend entry it is filled by: a cluster, or a kind of thing. */
  group: number;
  r: number;
  labelled: boolean;
  /** The company somebody typed: orange, and pinned in the middle. */
  centre: boolean;
  /** Where it starts, as a fraction of the canvas. */
  x0: number;
  y0: number;
  tip: Tip;
}

export interface DrawLink {
  s: number;
  t: number;
  /** Named together in findings, rather than a relation the ledger recorded. */
  dashed: boolean;
  width: number;
  /** The distance the simulation pulls the two ends towards. */
  length: number;
  tip: Tip;
}

export interface Drawing {
  /** Legend entries; the last is "Other", drawn as a hollow ring. */
  groups: string[];
  nodes: DrawNode[];
  links: DrawLink[];
}

/** "Northwind Lasers" for "Northwind Lasers GmbH" — a label, not a name. */
export function shortName(name: string): string {
  const cut = name.replace(
    /\s+(GmbH|AG|SE|KG|Inc\.?|Ltd\.?|LLC|Corporation|Corp\.?|Holding|S\.r\.l\.|B\.V\.|AB)\b.*$/,
    "",
  );
  return cut.length > 26 ? `${cut.slice(0, 25)}…` : cut;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/* ------------------------------------------------------------ the network */

/** The twelve most-cited companies carry a name; the rest say it on hover. */
const NAMED = 12;

export function networkDrawing(graph: MemoryGraph): Drawing {
  const G = graph.groups.length;
  const degree = graph.nodes.map(() => 0);
  graph.links.forEach(([s, t]) => {
    degree[s] = (degree[s] ?? 0) + 1;
    degree[t] = (degree[t] ?? 0) + 1;
  });
  const named = new Set(
    graph.nodes
      .map(([, , f], id) => ({ id, f }))
      .sort((a, b) => b.f - a.f)
      .slice(0, NAMED)
      .map((n) => n.id),
  );
  const nodes = graph.nodes.map(([name, g, f], id): DrawNode => {
    const angle = (g / G) * 2 * Math.PI;
    return {
      name,
      group: g,
      r: 3 + Math.sqrt(f) * 2,
      labelled: named.has(id),
      centre: false,
      x0: 0.5 + Math.cos(angle) * 0.3,
      y0: 0.5 + Math.sin(angle) * 0.3,
      tip: {
        title: name,
        lines: [
          { text: graph.groups[g] ?? "" },
          {
            text: `${plural(f, "finding")} · ${plural(degree[id] ?? 0, "connection")}`,
            tone: "mono",
          },
        ],
      },
    };
  });
  const links = graph.links.flatMap(([s, t, w]): DrawLink[] => {
    const a = graph.nodes[s];
    const b = graph.nodes[t];
    if (!a || !b) return [];
    return [
      {
        s,
        t,
        dashed: w > 0,
        width: w ? 1 + Math.min(w, 5) * 0.3 : 1.2,
        length: w ? 30 : 38,
        tip: {
          title: `${a[0]} and ${b[0]}`,
          lines: [
            w
              ? { text: `Named together in ${plural(w, "finding")}`, tone: "ink" }
              : { text: "Competitors, as a run recorded them", tone: "ink" },
            { text: "Type either name into the box to see all it is joined to.", tone: "muted" },
          ],
        },
      },
    ];
  });
  return { groups: graph.groups, nodes, links };
}

/* -------------------------------------------------------------- a company */

/**
 * What a company is joined to, by kind. Four kinds and Other — the brand's
 * five region greys, the same budget the clusters keep to.
 */
export const KINDS: { name: string; labels: string[] }[] = [
  { name: "Companies and investors", labels: ["Company", "Investor"] },
  { name: "Markets, industries, places", labels: ["Market", "Industry", "Geography"] },
  { name: "Products and technology", labels: ["Product", "Technology"] },
  { name: "People", labels: ["Person", "Expert"] },
  { name: "Other", labels: [] },
];

export function kindOf(label: string): number {
  const found = KINDS.findIndex((k) => k.labels.includes(label));
  return found === -1 ? KINDS.length - 1 : found;
}

/** Past this many named neighbours the labels run into each other. */
const NAMED_AROUND = 11;
/** Relations listed on one line's pop-up before "and n more". */
const RELATIONS_SHOWN = 4;

function footer(node: MemoryCompanyNode): TipLine | null {
  const parts = [
    node.findings != null ? plural(node.findings, "finding") : null,
    node.sources
      ? `${plural(node.sources, "source")}${node.source_kinds.length ? ` (${node.source_kinds.join(", ")})` : ""}`
      : null,
    node.seen ? `last recorded ${node.seen}` : null,
  ].filter(Boolean);
  return parts.length ? { text: parts.join(" · "), tone: "muted" } : null;
}

function nodeTip(node: MemoryCompanyNode, centre: boolean, around: TipLine[]): Tip {
  // What it is and how it is joined first; what is known about it after.
  const lines: TipLine[] = [
    { text: centre ? `${node.label} · the company you named` : node.label, tone: "muted" },
    ...around,
  ];
  if (node.aliases.length) lines.push({ text: `also ${node.aliases.join(", ")}`, tone: "muted" });
  if (node.description) lines.push({ text: node.description });
  if (node.facts.length) lines.push({ text: node.facts.map(([k, v]) => `${k} ${v}`).join(" · ") });
  for (const figure of node.figures ?? []) lines.push({ text: figure, tone: "mono" });
  const foot = footer(node);
  if (foot) lines.push(foot);
  return { title: node.name, lines };
}

export function companyDrawing(company: MemoryCompany): Drawing {
  const n = company.nodes.length;
  const linkOf = new Map(company.links.map((l) => [l.t, l]));
  const weight = (i: number) => {
    const l = linkOf.get(i);
    return l ? l.relations.length * 2 + l.findings : 0;
  };
  const named = new Set(
    company.nodes
      .map((_, i) => i)
      .filter((i) => i > 0)
      .sort((a, b) => weight(b) - weight(a) || a - b)
      .slice(0, NAMED_AROUND),
  );
  const together = (findings: number): TipLine => ({
    text: `Named together in ${plural(findings, "finding")}`,
  });

  const nodes = company.nodes.map((node, i): DrawNode => {
    const centre = i === 0;
    const angle = ((i - 1) / Math.max(1, n - 1)) * 2 * Math.PI - Math.PI / 2;
    const link = linkOf.get(i);
    const around: TipLine[] = link
      ? [
          ...link.relations.slice(0, RELATIONS_SHOWN - 1).map((r) => ({ text: r.sentence, tone: "ink" as const })),
          ...(link.relations.length > RELATIONS_SHOWN - 1
            ? [{ text: `and ${link.relations.length - (RELATIONS_SHOWN - 1)} more`, tone: "muted" as const }]
            : []),
          ...(link.findings ? [together(link.findings)] : []),
        ]
      : [];
    return {
      name: node.name,
      group: kindOf(node.label),
      r: centre ? 11 : Math.min(10, 4 + Math.sqrt(weight(i)) * 1.5),
      labelled: centre || named.has(i),
      centre,
      x0: centre ? 0.5 : 0.5 + Math.cos(angle) * 0.36,
      y0: centre ? 0.5 : 0.5 + Math.sin(angle) * 0.36,
      tip: nodeTip(node, centre, around),
    };
  });

  const links = company.links.flatMap((link): DrawLink[] => {
    const a = company.nodes[link.s];
    const b = company.nodes[link.t];
    if (!a || !b) return [];
    const lines: TipLine[] = [];
    for (const r of link.relations.slice(0, RELATIONS_SHOWN)) {
      lines.push({ text: r.sentence, tone: "ink" });
      if (r.description) lines.push({ text: r.description });
      const recorded = r.recorded
        ? `recorded ${r.recorded}${r.times > 1 ? `, ${r.times} times` : ""}`
        : r.times > 1
          ? `recorded ${r.times} times`
          : null;
      const when = [
        r.since ? `since ${r.since}` : null,
        r.until ? `until ${r.until}` : null,
        recorded,
      ].filter(Boolean);
      if (when.length) lines.push({ text: when.join(" · "), tone: "muted" });
    }
    if (link.relations.length > RELATIONS_SHOWN) {
      lines.push({ text: `and ${link.relations.length - RELATIONS_SHOWN} more`, tone: "muted" });
    }
    if (link.findings) {
      lines.push({ ...together(link.findings), tone: link.relations.length ? "muted" : "ink" });
    }
    return [
      {
        s: link.s,
        t: link.t,
        dashed: link.relations.length === 0,
        width: 1.2 + Math.min(link.relations.length, 4) * 0.4,
        length: link.relations.length ? 150 : 175,
        tip: { title: `${a.name} — ${b.name}`, lines },
      },
    ];
  });

  return { groups: KINDS.map((k) => k.name), nodes, links };
}
